import { TSituacaoFinanceira } from '../../../../../../types/api/financeiro/situacao-financeira';
import { TOpenModalSituacoesFinanceiras } from '..';
import { useMutation, useQueryClient } from 'react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import axiosClient from '../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import RadioSimplesSimOuNao from '../../../../../../components/RadioSimplesSimOuNao';

export default function DialogCriacaoEditSituacoes({
    defaultValue,
    open,
    handleClose
}: DialogCriacaoEditSituacoesProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])
    //

    const methods = useForm<TSituacaoFinanceira>({
        defaultValues: {
            id: valoresIniciais?.id,
            nome: valoresIniciais?.nome,
            considera_fechamento_cc: valoresIniciais?.considera_fechamento_cc || 'N',
            considera_fluxo_caixa: valoresIniciais?.considera_fluxo_caixa || 'N',
        }
    })
    const { control, handleSubmit, getFieldState, formState: { errors } } = methods;

    //Querys e mutations
    const { mutate: criarSituacao, isLoading: isLoadingCriarSituacao } = useMutation({
        mutationKey: 'cadastrar-situacao',
        mutationFn: (inputs: any) => axiosClient.post("financeiro/configuracoes/cadastrar-situacao", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar situação!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-situacoes"])
            handleClose()
            enqueueSnackbar('Cadastro de situação realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarSituacao, isLoading: isLoadingAttSituacao } = useMutation({
        mutationKey: 'atualizar-situacao',
        mutationFn: (inputs: any) => axiosClient.put(`financeiro/configuracoes/atualizar-situacao/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar situação!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-situacoes"])
            handleClose()
            enqueueSnackbar('Atualização de situação com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: TSituacaoFinanceira) => {
        const params = {
            nome: data.nome,
            id: data.id,
            considera_fechamento_cc: data.considera_fechamento_cc,
            considera_fluxo_caixa: data.considera_fluxo_caixa
        }
        if (isEdit) {
            atualizarSituacao(params)
        }
        if (!isEdit) {
            criarSituacao(params)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} situação financeira`}
                </DialogTitle>

                {
                    (isLoadingAttSituacao || isLoadingCriarSituacao) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttSituacao && !isLoadingCriarSituacao &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="id"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.id}
                                                    label="Código*"
                                                    variant="outlined"
                                                    type='number'
                                                    fullWidth
                                                    error={getFieldState('id').invalid}
                                                    helperText={getFieldState('id').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 6}}>
                                        <Controller
                                            name="nome"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.nome}
                                                    label="Nome*"
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('nome').invalid}
                                                    helperText={getFieldState('nome').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 7}}>
                                        <RadioSimplesSimOuNao
                                            {...{ valoresIniciais }}
                                            nomeForm='considera_fechamento_cc'
                                            nomeCampo='Considera fechamento C/C Consumo?'
                                        />
                                    </Grid>
                                    <Grid size={{xs: 5}}>
                                        <RadioSimplesSimOuNao
                                            {...{ valoresIniciais }}
                                            nomeForm='considera_fluxo_caixa'
                                            nomeCampo='Considera fluxo caixa?'
                                        />
                                    </Grid>
                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarSituacao || isLoadingAttSituacao}

                                    >
                                        {
                                            (isLoadingCriarSituacao || isLoadingAttSituacao) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogCriacaoEditSituacoesProps {
    defaultValue: TOpenModalSituacoesFinanceiras;
    open: boolean;
    handleClose: () => void;

}
